


import React, { useState } from "react";
import { Button, notification } from "antd";
import { IoPauseCircleOutline, IoPlayCircleOutline } from "react-icons/io5";
import dayjs from "dayjs";
import { POST } from "../../../../utils/apiRequest";
import { API } from "../../../../config/Api";
import PauseResumeModal from "./pauseResumeModal";


interface HoldResumeType {
  id?: string;
  hold_from: string;
  hold_to: string;
  num_days: number;
  current_exp: string;
  new_exp?: string;
}

interface UserData {
  user: {
    id: string;
    end_date: string;
  };
  holdResume?: HoldResumeType;
  office: {
    id: string;
  };
}

interface PauseScreenProps {
  data: UserData;
  getUser: () => void;
}

const PauseScreen: React.FC<PauseScreenProps> = ({ data, getUser }) => {
  const holdResume = data?.holdResume;
  const [activeModal, setActiveModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isPaused = Boolean(holdResume?.id);

  const calculateNewExpiry = (from: dayjs.Dayjs, to: dayjs.Dayjs, days: number) => {
    const today = dayjs().startOf("day");
    let newExp;

    if (!isPaused) {
      // For pause action - add days to current expiry
      newExp = dayjs(data?.user?.end_date).add(days, "days");
    } else {
      // For resume action - complex calculation based on scenarios
      const holdFrom = dayjs(holdResume?.hold_from).startOf("day");
      const holdTo = dayjs(holdResume?.hold_to).startOf("day");
      const currentExp = dayjs(holdResume?.current_exp);
      const numDays = Number(holdResume?.num_days);

      if (holdFrom.isSame(today, "date")) {
        newExp = currentExp;
      } else if (today.isAfter(holdFrom)) {
        const diff = today.diff(holdTo, "day");
        newExp = currentExp.add(diff, "day");
      } else if (holdFrom.isAfter(today)) {
        newExp = currentExp;
      } else {
        const dayDiff = today.diff(holdFrom, "days");
        const reduceDays = numDays - dayDiff;
        if (numDays === 1 && Math.abs(dayDiff) === 1) {
          newExp = currentExp.subtract(1, "day");
        } else {
          newExp = currentExp.subtract(reduceDays, "day");
        }
      }
    }

    return newExp;
  };

  const submit = async (from: dayjs.Dayjs, to: dayjs.Dayjs, days: number) => {
    try {
      setIsLoading(true);
      const newExp = calculateNewExpiry(from, to, days);

      const payload = {
        user_id: data?.user?.id,
        current_exp: isPaused ? holdResume?.current_exp : data?.user?.end_date,
        hold_from: isPaused ? holdResume?.hold_from : from.format("YYYY-MM-DD"),
        hold_to: isPaused ? holdResume?.hold_to : to.format("YYYY-MM-DD"),
        num_days: days,
        type: isPaused ? "resume" : "pause",
        office_id: data?.office?.id,
        new_exp: newExp.format("YYYY-MM-DD"),
        // hold_resume_id: isPaused ? holdResume?.id : undefined,
      };

      const response:any = await POST(API.HOLD_RESUME_ADD, payload);

      if (response?.status) {
        notification.success({
          message: "Success",
          description: isPaused
            ? "Subscription resumed successfully."
            : "Subscription paused successfully.",
        });
        getUser();
        setActiveModal(false);
      } else {
        throw new Error(response?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error in submit:", error);
      notification.error({
        message: "Error",
        description: error instanceof Error ? error.message : "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Button
        className={`d-flex align-items-center ${
          isPaused ? "text-success" : "text-warning"
        }`}
        onClick={() => setActiveModal(true)}
        style={{
          height: "auto",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "8px 12px",
        }}
      >
        <div className="d-flex align-items-center">
          {isPaused ? (
            <IoPlayCircleOutline className="me-2" size={18} />
          ) : (
            <IoPauseCircleOutline className="me-2" size={18} />
          )}
          <span>{isPaused ? "Resume" : "Pause"} Subscription</span>
        </div>
        {isPaused && holdResume && (
          <div className="small text-muted mt-1">
            {`Hold period ${dayjs(holdResume.hold_from).format(
              "DD MMMM"
            )} to ${dayjs(holdResume.hold_to).format("DD MMMM")} - ${
              holdResume.num_days
            } days`}
          </div>
        )}
      </Button>

      <PauseResumeModal
        data={data}
        isModalOpen={activeModal}
        handleCancel={() => setActiveModal(false)}
        onSubmit={submit}
        holdResume={holdResume}
        isLoading={isLoading}
        isPaused={isPaused}
      />
    </div>
  );
};

export default PauseScreen;
